import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Control } from 'react-redux-form';
import {
  DateInputReduxFormField,
  DollarInputFormField,
  DropDownReduxFormField,
  GoogleAddressSearch,
  TextAreaFormField,
  TextInputFormField,
  XIcon,
  SimpleModal
} from '@forwardfinancing/component-library';
import { trackUserInput } from '@forwardfinancing/analytics-frontend-library';
import StipsUploader from '../AttachmentUploader/StipsUploader';
import RadioButtonFormField from '../RadioButtonFormField/RadioButtonFormField';
import utils from '../../../lib/utils';
import linkUtils from '../../../lib/linkUtils';
import moment from 'moment';
import {
  GOOGLE_API_KEY,
  GOOGLE_API_RETRIES,
  BUSINESS_INFO_GOOGLE_AUTOCOMPLETE
} from '../../../constants/googleAPI';
import CheckboxField from '../Checkbox/CheckboxField';
import colors from '../../../constants/colors';

class FormField extends Component {

  renderGroupTitle(field) {
    const className="submission-form__field-group-title title is-5";
    return (
      <div key={field.display} className={className}>
        {field.display}
      </div>
    );
  }

  renderGroupSubtitle(field) {
    const className="submission-form__field-group-subtitle is-6";
    return (
      <div key={field.display} className={className}>
        {field.display}
      </div>
    );
  }

  onSelectHandler(args) {
    this.props.onSelect(args);
    if (this.props.field.modelVal === BUSINESS_INFO_GOOGLE_AUTOCOMPLETE.MODEL){
      const {accountDetails, accountAddress} = args;
      if (accountDetails.name) {
        trackUserInput({
          label: "Google Business Lookup",
          googleMatch: [accountDetails.name, accountAddress.street1, accountAddress.city, accountAddress.state].join(", "),
          page: linkUtils.capitalizeFirstLetter(this.props.title)
        });
      }
    }
  }

  renderAddressSearch(field) {

    let containerClass = "";
    if ("ownerIndex" in field){
      containerClass = "owner-address-lookup";
    } else if (field.modelVal === BUSINESS_INFO_GOOGLE_AUTOCOMPLETE.MODEL) {
      containerClass = "business-lookup";
    }

    return (
      <span key={field.modelVal}>
        <div className={containerClass}>
          <GoogleAddressSearch
            attributeName={field.modelVal}
            desiredClassName={field.classForLabel}
            displayName={field.display}
            errorMessage="Address not found, please enter manually into the fields below to continue"
            googleApiKey={GOOGLE_API_KEY()}
            googleApiRetries={GOOGLE_API_RETRIES()}
            {...field.labels}
            modelName={this.props.modelName}
            placeHolder={field.placeHolder}
            onSelect={this.onSelectHandler.bind(this)}
            refreshUUID={this.props.addressActions.refreshUUID}
            uuid={this.props.uuid}
            inputSize={null}
            lookupDescription={field.additionInfo}
          />
        </div>
      </span>
    );
  }

  renderTextInput(field) {
    return (
      <span key={field.modelVal} className={field.className}>
        <TextInputFormField
          displayName={field.display}
          attributeName={field.modelVal}
          onChange={this.props.onChange}
          modelName={this.props.modelName}
          isRequired={field.required}
          desiredClassName={this.additionalClassForLabel(field.required) + " submission-form"}
          useMask={field.useMask}
          placeHolder={field.placeHolder}
          maxLength={field.maxLength}
          validateLength={field.validateLength}
          lengthMessage={field.lengthMessage}
          validations={field.validations}
          textBoxSuffix={field.suffix}
        />
      </span>
    );
  }

  renderTextAreaInput(field) {
    return (
      <span key={field.modelVal}>
        <TextAreaFormField
          displayName={field.display}
          attributeName={field.modelVal}
          modelName={this.props.modelName}
          isRequired={field.required}
          desiredClassName="submission-form"
          placeHolder={field.placeHolder}
        />
      </span>
    );
  }

  renderDollarInput(field) {
    return (
      <span key={field.modelVal}>
        <DollarInputFormField
          displayName={field.display}
          attributeName={field.modelVal}
          modelName={this.props.modelName}
          isRequired={field.required}
          desiredClassName="submission-form"
          placeHolder={field.placeHolder}
          maxLength={field.maxLength}
          needsInstruction={field.needsInstruction}
        />
      </span>
    );
  }

  renderDropDown(field) {
    return (
      <span key={field.modelVal}>
        <DropDownReduxFormField
          displayName={field.display}
          attributeName={field.modelVal}
          modelName={this.props.modelName}
          isRequired={field.required}
          optionsData={field.options}
          onChangeOfDropDown={this.props.onChange}
          desiredClassName={this.additionalClassForLabel(field.required) + " submission-form"}
          type={field.type}
        />
      </span>
    );
  }

  renderRadioButtons(field) {
    return (
      <span key={field.modelVal}>
        <RadioButtonFormField
          displayName={field.display}
          attributeName={field.modelVal}
          modelName={this.props.modelName}
          isRequired={field.required}
          optionsData={field.options}
          desiredClassName="submission-form"
        />
      </span>
    );
  }

  renderExistingFinancingCheckbox(field) {
    return(
      <span key={field.modelVal}>
        <CheckboxField
          displayName={field.display}
          attributeName={field.modelVal}
          modelName={this.props.modelName}
          desiredClassName="submission-form"
        />
      </span>
    );
  }

  renderDatePicker({modelVal, display, required, className}) {
    return (
      <span key={modelVal} className={className}>
        <DateInputReduxFormField
          displayName={display}
          attributeName={modelVal}
          modelName={this.props.modelName}
          isRequired={required}
          desiredClassName={this.additionalClassForLabel(required) + " submission-form"}
          validatorFunction={(value) => utils.validateDateInput(moment(value, 'MM/DD/YYYY'), true)}
        />
      </span>
    );
  }

  renderFileUploader(field) {
    return (
      <span key={field.modelVal}>
        <StipsUploader {...this.props} />
      </span>
    );
  }

  labelText(displayText) {
    return(
      <label className="submission-form__field-label label required">
        {displayText} *
      </label>
    );
  }

  renderSameDbaAndLegalNameCheckbox(field) {
    const model = `${this.props.modelName}.${field.otherVal}`;
    return(
      <div className="text-input-form-field field">
        <div className="submission-form__field-group field">
          {this.labelText(field.display)}
          <span className="submission-form__field-control control">
            <label>
              <Control.checkbox
                model={model}
                className="submission-form__field-control control checkbox-legal"/>
              Same as Business DBA
            </label>
          </span>
        </div>
      </div>
    );
  }

  renderBusinessLegal(field) {
    if(this.props.submission.sameDbaAndLegalName) {
      return(
        <span key={field.modelVal}>
          {this.renderSameDbaAndLegalNameCheckbox(field)}
        </span>
      );
    } else {
      return(
        <span key={field.modelVal}>
          {this.renderSameDbaAndLegalNameCheckbox(field)}
          <TextInputFormField
            displayName={null}
            attributeName={field.modelVal}
            onChange={this.props.onChange}
            modelName={this.props.modelName}
            isRequired={field.required}
            desiredClassName="submission-form"
            useMask={field.useMask}
            placeHolder={field.placeHolder}
            maxLength={field.maxLength}
            validateLength={field.validateLength}
            lengthMessage={field.lengthMessage}
            validations={field.validations}
            errorMessageName={field.display}
          />
        </span>
      );
    }
  }

  renderAddOwnerButton() {
    return (
      <div className="submission-form__add-more-owners"
        onClick={this.props.addOwner.bind(this)}>
          ＋ Add Owner
      </div>
    );
  }

  renderCloseButton(field) {
    return ( this.props.closeButtonAction ?
      <div className="submission-form__close-button-wrapper"
        id={field.index}
      >
        <button
          className="submission-form__close-button"
          type="button"
          onClick={() => this.props.closeButtonAction(field.index)}
        >
          <XIcon
            fill={colors.red}
            width="32"
            strokeWidth="2"
            />
        </button>
      </div>
      :
      ''
    );
  }

  renderConfirmationModal() {
    const id  = this.props.ownerToRemove;
    // render modal if id exists
    if (id !== null) {
      const owner = this.props.submission.owners[id];
      const nameLine = [owner.first_name, owner.last_name];
      const addressLine = [owner.city, owner.state, owner.zip];
      return (<SimpleModal
        isActive={true}
        title="Please Confirm"
        desiredClassName="submission-form__confirm-delete-modal"
        simpleMessageList={[
          `You are about to remove Owner ${parseInt(this.props.ownerToRemove, 10) + 1} from the application.`,
          nameLine.filter((i) => i).join(' '),
          owner.street1,
          addressLine.filter((i) => i).join(' ')
        ]}
        actions={{
          primary: {
            label: "Cancel",
            action: this.props.cancelRemoveOwner
          },
          secondary: {
            label: "Remove",
            action: () => this.props.removeOwner(this.props.ownerToRemove)
          }
        }}
      />);
    } else {
      return '';
    }
  }

  additionalClassForLabel(required) {
    let additionalClass = "";
    if(required) {
      additionalClass = "required";
    }
    return additionalClass;
  }

  render() {
    const { field } = this.props;
    // lazy evaluation is the process of returning a function
    // due to this the below functions must be bound
    return (
      {
        'addressSearch': this.renderAddressSearch.bind(this),
        'datepicker': this.renderDatePicker.bind(this),
        'dollar': this.renderDollarInput.bind(this),
        'dropdown': this.renderDropDown.bind(this),
        'file': this.renderFileUploader.bind(this),
        'radio': this.renderRadioButtons.bind(this),
        'text': this.renderTextInput.bind(this),
        'textarea': this.renderTextAreaInput.bind(this),
        'title': this.renderGroupTitle.bind(this),
        'businessLegal': this.renderBusinessLegal.bind(this),
        'existingFinancing': this.renderExistingFinancingCheckbox.bind(this),
        'addOwnerButton': this.renderAddOwnerButton.bind(this),
        'closeButton': this.renderCloseButton.bind(this),
        'subtitle': this.renderGroupSubtitle.bind(this),
        'removeOwnerModal': this.renderConfirmationModal.bind(this)
      }[field.inputType](field)
    );
  }
}

FormField.propTypes = {
  addressActions: PropTypes.object,
  field: PropTypes.object.isRequired,
  modelName: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onSelect: PropTypes.func,
  uuid: PropTypes.string,
  title: PropTypes.string,
  handlesameDbaAndLegalName: PropTypes.func,
  submission: PropTypes.object,
  addOwner: PropTypes.func,
  closeButtonAction: PropTypes.func,
  removeOwner: PropTypes.func,
  ownerToRemove: PropTypes.number,
  cancelRemoveOwner: PropTypes.func
};

export default FormField;
